import { useState, lazy, Suspense, Component, ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import { QuoteFormProvider } from "./components/QuoteFormContext";
import PremiumLoader from "./components/PremiumLoader";
import VisitPopup from "./components/VisitPopup";
import ChatWidget from "./components/ChatWidget";
import ReadingProgress from "./components/ReadingProgress";

// Route-level code splitting: keep the home page eager for fastest LCP,
// lazy-load every secondary route so it ships as its own chunk.
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// A stale deployment can leave the browser requesting a chunk that no longer
// exists, which would blank #root. Catch that, reload once (with a cooldown so
// we never loop), and otherwise render the current page.
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: unknown) {
    const msg = String((error as Error)?.message || error);
    const isChunkError = /Loading chunk|dynamically imported module|Failed to fetch/i.test(msg);
    const KEY = "chunk-reload-ts";
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (isChunkError && Date.now() - last > 10000) {
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
    }
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4 text-center">
          <div>
            <p className="font-heading text-xl font-bold mb-2">Something went wrong loading this page.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 rounded-lg bg-primary px-5 py-2 text-primary-foreground font-semibold"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const App = () => {
  const [loaderDone, setLoaderDone] = useState(false);
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <QuoteFormProvider>
        <PremiumLoader onDone={() => setLoaderDone(true)} />
        <VisitPopup ready={loaderDone} />
        <ChatWidget />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ReadingProgress />
          <ScrollToTop />
          <ChunkErrorBoundary>
            <Suspense fallback={<div className="min-h-screen bg-background" aria-hidden="true" />}>
              <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/about" element={<About />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/locations/:slug" element={<LocationDetail />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ChunkErrorBoundary>
        </BrowserRouter>
      </QuoteFormProvider>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
